import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { ArrowLeft, Save } from 'lucide-react';
import {
  FormEditor,
  emptyFormValues,
  validateForm,
  type FormEditorValues,
} from '~/components/FormEditor/FormEditor';
import { useCreateForm } from '~/lib/forms-queries';
import { useTenantContext } from '~/lib/tenantContext';
import { formatPbError } from '~/lib/pb-error';
import styles from './_authed.automations.templates_.editor.module.css';

export const Route = createFileRoute('/_authed/automations/forms_/new')({
  component: NewFormPage,
});

function NewFormPage() {
  const navigate = useNavigate();
  const { tenants, selectedTenantId } = useTenantContext();
  const create = useCreateForm();

  // Defaultear al cliente activo (no al primero de la lista).
  const defaultTenant = selectedTenantId ?? tenants[0]?.id ?? '';
  const [values, setValues] = useState<FormEditorValues>(() =>
    emptyFormValues(defaultTenant),
  );
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    setError(null);
    const validationError = validateForm(values);
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      const created = await create.mutateAsync({
        tenant: values.tenant,
        name: values.name,
        slug: values.slug,
        fields_schema: values.fields_schema,
        default_flow: values.default_flow || undefined,
        thank_you: values.thank_you,
        active: values.active,
      });
      void navigate({
        to: '/automations/forms/$formId',
        params: { formId: created.id },
      });
    } catch (err) {
      setError(formatPbError(err, 'Error al guardar el formulario'));
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link to="/automations/forms" className={styles.backLink}>
          <ArrowLeft size={14} aria-hidden /> Formularios
        </Link>
        <div className={styles.titleRow}>
          <h2 className="h4" style={{ margin: 0 }}>Nuevo formulario</h2>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={handleSave}
              disabled={create.isPending}
            >
              <Save size={14} aria-hidden />
              {create.isPending ? 'Creando…' : 'Crear formulario'}
            </button>
          </div>
        </div>
        {error && <p className={styles.errorBanner}>{error}</p>}
      </header>

      <FormEditor
        mode="new"
        values={values}
        onChange={setValues}
        tenants={tenants}
        saving={create.isPending}
      />
    </div>
  );
}
